import React, { useEffect, useState, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import UploadImg from "../../assets/images/icons/upload-img.svg";

const getMediaUrl = (path) => {
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = (process.env.REACT_APP_API || "http://quytech.in:5001").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

function SaudiaMoment() {
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [existingDesktopImage, setExistingDesktopImage] = useState(null);
  const [existingMobileImage, setExistingMobileImage] = useState(null);
  const [desktopFile, setDesktopFile] = useState(null);
  const [mobileFile, setMobileFile] = useState(null);
  const [desktopPreview, setDesktopPreview] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(null);
  const [titleEnglish, setTitleEnglish] = useState("");
  const [titleArabic, setTitleArabic] = useState("");
  const [descriptionEnglish, setDescriptionEnglish] = useState("");
  const [descriptionArabic, setDescriptionArabic] = useState("");
  const [momentId, setMomentId] = useState(null);
  const desktopCurrentUrl = existingDesktopImage ? getMediaUrl(existingDesktopImage) : null;
  const mobileCurrentUrl = existingMobileImage ? getMediaUrl(existingMobileImage) : null;

  const fetchSaudiaMoment = useCallback(async () => {
    setFetching(true);
    try {
      const response = await handleApiCall("get", endpoints.saudiaMomentList);
      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data || response?.data;
        // Handle array response (list) or single object
        const momentData = Array.isArray(payload) && payload.length > 0 ? payload[0] : payload;
        const desktopImage = momentData?.desktop_image || momentData?.image || momentData?.image_url || momentData?.url || "";
        const mobileImage = momentData?.mobile_image || momentData?.mobileImage || "";
        const id = momentData?._id || momentData?.id || null;

        setExistingDesktopImage(desktopImage || null);
        setExistingMobileImage(mobileImage || null);
        setDesktopFile(null);
        setMobileFile(null);
        setDesktopPreview(null);
        setMobilePreview(null);
        setTitleEnglish(momentData?.title_english || momentData?.titleEnglish || "");
        setTitleArabic(momentData?.title_arabic || momentData?.titleArabic || "");
        setDescriptionEnglish(momentData?.discription_english || momentData?.description_english || "");
        setDescriptionArabic(momentData?.discription_arabic || momentData?.description_arabic || "");
        setMomentId(id);
      } else if (response?.error) {
        console.error("Error fetching Saudia Moment:", response?.message);
      }
    } catch (error) {
      console.error("Error fetching Saudia Moment:", error);
      toast.error("Failed to fetch Saudia Moment");
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchSaudiaMoment();
  }, [fetchSaudiaMoment]);

  const validateImageFile = (selected) => {
    if (!selected.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return false;
    }
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selected.size > maxSize) {
      toast.error("Image file size should not exceed 10MB");
      return false;
    }
    return true;
  };

  const handleDesktopImageChange = (e) => {
    const selected = e.target.files[0];
    if (selected && validateImageFile(selected)) {
      setDesktopFile(selected);
      const reader = new FileReader();
      reader.onload = (event) => setDesktopPreview(event.target.result);
      reader.readAsDataURL(selected);
    }
  };

  const handleMobileImageChange = (e) => {
    const selected = e.target.files[0];
    if (selected && validateImageFile(selected)) {
      setMobileFile(selected);
      const reader = new FileReader();
      reader.onload = (event) => setMobilePreview(event.target.result);
      reader.readAsDataURL(selected);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!momentId) {
      toast.error("Unable to update: Moment ID not found");
      return;
    }

    const trimmedTitleEnglish = titleEnglish.trim();
    const trimmedTitleArabic = titleArabic.trim();
    const trimmedDescriptionEnglish = descriptionEnglish.trim();
    const trimmedDescriptionArabic = descriptionArabic.trim();

    // Validate title (English)
    if (!trimmedTitleEnglish) {
      toast.error("Please enter title in English");
      return;
    }

    if (trimmedTitleEnglish.length < 3) {
      toast.error("Title (English) should be at least 3 characters long");
      return;
    }

    if (trimmedTitleEnglish.length > 100) {
      toast.error("Title (English) should not exceed 100 characters");
      return;
    }


    // Validate title (Arabic)
    if (!trimmedTitleArabic) {
      toast.error("Please enter title in Arabic");
      return;
    }

    if (trimmedTitleArabic.length < 3) {
      toast.error("Title (Arabic) should be at least 3 characters long");
      return;
    }

    if (trimmedTitleArabic.length > 100) {
      toast.error("Title (Arabic) should not exceed 100 characters");
      return;
    }


    // Validate descriptions
    if (!trimmedDescriptionEnglish || !trimmedDescriptionArabic) {
      toast.error("Please fill in all description fields");
      return;
    }

    try {
      setLoading(true);
      const submitData = new FormData();
      if (desktopFile) {
        submitData.append("desktop_image", desktopFile);
      }
      if (mobileFile) {
        submitData.append("mobile_image", mobileFile);
      }
      submitData.append("title_english", trimmedTitleEnglish);
      submitData.append("title_arabic", trimmedTitleArabic);
      submitData.append("discription_english", trimmedDescriptionEnglish);
      submitData.append("discription_arabic", trimmedDescriptionArabic);
      const response = await handleApiCall("patch", `${endpoints.updateSaudiaMoment}/${momentId}`, submitData);
      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success("Saudia Moment updated successfully!");
        setDesktopFile(null);
        setDesktopPreview(null);
        setMobileFile(null);
        setMobilePreview(null);
        fetchSaudiaMoment();
      } else if (response?.error) {
        console.error("Error updating Saudia Moment:", response?.message);
      }
    } catch (error) {
      console.error("Error updating Saudia Moment:", error);
      toast.error("Failed to update Saudia Moment");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
              <div className="spinner-border" role="status">
                <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Saudia Moment</h3>
            </div>
            <div className="mb-4 px-0 py-2">
              <form onSubmit={handleSubmit}>
                <div className="card-heading d-flex align-items-center mb-3 pb-2">
                  <div className="d-flex align-items-center justify-content-start col-md-12 px-0">
                    <Button type="submit" className="rounded-lg" disabled={loading}>
                      {loading ? UI_CONSTANTS.BUTTON_LABELS.UPDATING : UI_CONSTANTS.BUTTON_LABELS.UPDATE}
                    </Button>
                  </div>
                </div>
                <div className="row mx-n2">
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">Title (English)</label>
                    <input
                      type="text"
                      className="form-control default"
                      value={titleEnglish}
                      onChange={(event) => setTitleEnglish(event.target.value)}
                      placeholder="Enter English title"
                      maxLength={100}
                      required
                    />
                  </div>
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">Title (Arabic)</label>
                    <input
                      type="text"
                      className="form-control default"
                      value={titleArabic}
                      onChange={(event) => setTitleArabic(event.target.value)}
                      placeholder="Enter Arabic title"
                      dir="rtl"
                      maxLength={100}
                      required
                    />
                  </div>
                </div>
                <div className="row mx-n2">
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">Description (English)</label>
                    <textarea
                      className="form-control default"
                      rows="5"
                      value={descriptionEnglish}
                      onChange={(event) => setDescriptionEnglish(event.target.value)}
                      placeholder="Enter English description"
                      required
                    />
                  </div>
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">Description (Arabic)</label>
                    <textarea
                      className="form-control default"
                      rows="5"
                      value={descriptionArabic}
                      onChange={(event) => setDescriptionArabic(event.target.value)}
                      placeholder="Enter Arabic description"
                      dir="rtl"
                      required
                    />
                  </div>
                </div>
                <div className="row mx-n2">
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">Current Desktop Image</label>
                    {desktopCurrentUrl ? (
                      <div className="mb-3">
                        <img src={desktopCurrentUrl} alt="Desktop Saudia Moment" style={{ width: "100%", maxHeight: "320px", objectFit: "contain", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }} />
                      </div>
                    ) : (
                      <div className="text-muted mb-3">No desktop image available.</div>
                    )}
                  </div>
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">Current Mobile Image</label>
                    {mobileCurrentUrl ? (
                      <div className="mb-3">
                        <img src={mobileCurrentUrl} alt="Mobile Saudia Moment" style={{ width: "100%", maxHeight: "320px", objectFit: "contain", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }} />
                      </div>
                    ) : (
                      <div className="text-muted mb-3">No mobile image available.</div>
                    )}
                  </div>
                </div>
                <div className="row mx-n2">
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2 d-block text-center">Upload Desktop Image</label>
                    <div className="col-md-12 tab-content row px-0 mx-0">
                      <label className="imageUpload d-flex align-items-center justify-content-center" role="button">
                        <input type="file" hidden accept="image/*" onChange={handleDesktopImageChange} />
                        {desktopPreview ? (
                          <div className="position-relative">
                            <img src={desktopPreview} alt="Desktop preview" style={{ width: "220px", height: "180px", objectFit: "cover", borderRadius: "8px" }} />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger position-absolute"
                              style={{ top: "-5px", right: "-5px", padding: "2px 6px", fontSize: "10px" }}
                              onClick={(event) => {
                                event.preventDefault();
                                setDesktopFile(null);
                                setDesktopPreview(null);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <>
                            <img src={UploadImg} className="icon" alt="Upload desktop" />
                            <div className="text-white mt-1">Upload desktop image</div>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2 d-block text-center">Upload Mobile Image</label>
                    <div className="col-md-12 tab-content row px-0 mx-0">
                      <label className="imageUpload d-flex align-items-center justify-content-center" role="button">
                        <input type="file" hidden accept="image/*" onChange={handleMobileImageChange} />
                        {mobilePreview ? (
                          <div className="position-relative">
                            <img src={mobilePreview} alt="Mobile preview" style={{ width: "220px", height: "180px", objectFit: "cover", borderRadius: "8px" }} />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger position-absolute"
                              style={{ top: "-5px", right: "-5px", padding: "2px 6px", fontSize: "10px" }}
                              onClick={(event) => {
                                event.preventDefault();
                                setMobileFile(null);
                                setMobilePreview(null);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <>
                            <img src={UploadImg} className="icon" alt="Upload mobile" />
                            <div className="text-white mt-1">Upload mobile image</div>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SaudiaMoment;

